import { Component, OnDestroy, ViewChild } from '@angular/core';
import {
  PoBreadcrumb,
  PoDialogService,
  PoNotificationService,
  PoUploadComponent,
  PoUploadFileRestrictions
} from '@po-ui/ng-components';
import { Subscription, firstValueFrom, timer } from 'rxjs';
import { exhaustMap, takeWhile } from 'rxjs/operators';
import { DpFichasService } from './dp-fichas.service';
import {
  DpFichaConsultaResultado,
  DpPdfArquivoEnvio,
  DpPdfLoteEnvio,
  DpPdfProcessoItem,
  ImportDpLoteResult,
  ImportDpPdfResult
} from './dp-fichas.models';

type ArquivoUpload = { name: string; rawFile?: File; status?: number };

@Component({
  selector: 'app-dp-fichas',
  templateUrl: './dp-fichas.component.html',
  styleUrls: ['./dp-fichas.component.css'],
  standalone: false
})
export class DpFichasComponent implements OnDestroy {
  @ViewChild('uploadPdf') uploadPdf!: PoUploadComponent;

  loading = false;
  showProgress = false;
  modoPasta = false;
  processos: DpPdfProcessoItem[] = [];
  inicioProcessamento: Date | null = null;
  fimProcessamento: Date | null = null;

  readonly pdfRestrictions: PoUploadFileRestrictions = {
    allowedExtensions: ['.pdf', '.zip'],
    maxFileSize: 180 * 1024 * 1024
  };
  readonly urlPlaceholder = 'https://po-ui.io';

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'DP' },
      { label: 'Fichas de empregados' }
    ]
  };

  private readonly pollSubs = new Map<string, Subscription>();
  private readonly pollIntervalMs = 3000;
  private readonly pollMaxTentativas = 80;
  /** Abaixo do limite de 200 MB por requisição da API. */
  private readonly limiteLoteBytes = 180 * 1024 * 1024;

  constructor(
    private readonly dpFichasService: DpFichasService,
    private readonly poDialogService: PoDialogService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  ngOnDestroy(): void {
    this.pararTodosPollings();
  }

  importarPdfs(): void {
    if (this.loading) return;

    const files: ArquivoUpload[] = this.uploadPdf?.currentFiles ?? [];
    if (!files.length) {
      this.poNotificationService.warning(
        this.modoPasta
          ? 'Selecione uma pasta com PDFs de fichas de empregados.'
          : 'Selecione ao menos um PDF de ficha de empregado.'
      );
      return;
    }

    const lotes = this.montarLotes(files);
    const message = this.modoPasta
      ? `Enviar ${files.length} PDF(s) de ${lotes.length} pasta(s) para processamento no Domínio?\n\n` +
        lotes.slice(0, 15).map((l) => `${l.nome} (${l.arquivos.length} PDF)`).join('\n') +
        (lotes.length > 15 ? `\n... e mais ${lotes.length - 15} pasta(s)` : '')
      : `Enviar ${files.length} PDF(s) de ficha para processamento no Domínio?\n\n${files.map((f) => f.name).join(', ')}`;

    this.poDialogService.confirm({
      title: 'Confirmar importação',
      message,
      confirm: () => void this.executarImportacao(files, lotes)
    });
  }

  alternarModoPasta(): void {
    this.uploadPdf?.clear();
  }

  getStatusLabel(status: DpPdfProcessoItem['status']): string {
    if (status === 'PRONTO') return 'Concluído';
    if (status === 'PARCIAL') return 'Parcial';
    if (status === 'BLOQUEADO') return 'Erro';
    return 'Processando';
  }

  /**
   * Um envio por subpasta da pasta escolhida (normalmente uma empresa), para acompanhar o progresso por pasta.
   * Arquivos avulsos vão num envio só. Um envio que passaria do limite da API é dividido em partes.
   */
  private montarLotes(files: ArquivoUpload[]): DpPdfLoteEnvio[] {
    const grupos = new Map<string, DpPdfArquivoEnvio[]>();
    for (const f of files) {
      if (!f.rawFile) continue;

      const partes = (f.rawFile.webkitRelativePath || '').split('/').filter(Boolean);
      const grupo = partes.length > 2 ? partes[1] : partes.length === 2 ? partes[0] : 'PDFs selecionados';
      const arquivos = grupos.get(grupo) ?? [];
      arquivos.push({ file: f.rawFile, nome: this.nomeEnvio(f) });
      grupos.set(grupo, arquivos);
    }

    const lotes: DpPdfLoteEnvio[] = [];
    for (const [nome, arquivos] of grupos) {
      const partes: DpPdfArquivoEnvio[][] = [];
      let atual: DpPdfArquivoEnvio[] = [];
      let tamanho = 0;
      for (const arquivo of arquivos) {
        if (atual.length && tamanho + arquivo.file.size > this.limiteLoteBytes) {
          partes.push(atual);
          atual = [];
          tamanho = 0;
        }
        atual.push(arquivo);
        tamanho += arquivo.file.size;
      }
      if (atual.length) partes.push(atual);

      partes.forEach((p, i) =>
        lotes.push({ nome: partes.length > 1 ? `${nome} (parte ${i + 1} de ${partes.length})` : nome, arquivos: p })
      );
    }

    return lotes;
  }

  /** Caminho dentro da pasta escolhida (ex.: "Empresa A/Ficha.pdf"), para identificar o arquivo nos erros. */
  private nomeEnvio(f: ArquivoUpload): string {
    const partes = (f.rawFile?.webkitRelativePath || '').split('/').filter(Boolean);
    return partes.length > 1 ? partes.slice(1).join('/') : f.name;
  }

  private async executarImportacao(files: ArquivoUpload[], lotes: DpPdfLoteEnvio[]): Promise<void> {
    this.pararTodosPollings();
    this.loading = true;
    this.showProgress = true;
    this.inicioProcessamento = new Date();
    this.fimProcessamento = null;

    const invalidos: DpPdfProcessoItem[] = files
      .filter((f) => !f.rawFile)
      .map((f) => ({ nomeArquivo: f.name, status: 'BLOQUEADO' as const, mensagem: 'Arquivo inválido.' }));

    if (!lotes.length) {
      this.processos = invalidos;
      this.loading = false;
      this.poNotificationService.error('Nenhum PDF válido para enviar.');
      this.tentarFinalizarProgresso();
      return;
    }

    const uploads = new Map(files.filter((f) => !!f.rawFile).map((f) => [this.nomeEnvio(f), f] as const));
    const totalPdfs = lotes.reduce((total, l) => total + l.arquivos.length, 0);
    const progresso: DpPdfProcessoItem = {
      nomeArquivo: `${totalPdfs} PDF(s)`,
      status: 'PROCESSANDO',
      mensagem: 'Enviando e lendo os PDFs...'
    };
    this.processos = [progresso, ...invalidos];

    for (const [indice, lote] of lotes.entries()) {
      if (lotes.length > 1) {
        progresso.mensagem = `Enviando ${lote.nome} (${indice + 1} de ${lotes.length})...`;
      }

      const itens = await this.enviarLote(lote, uploads);
      this.processos.push(...itens);
      for (const item of itens) {
        if (item.status !== 'BLOQUEADO' && item.requestId) {
          this.iniciarPolling(item);
        }
      }
    }

    this.processos = this.processos.filter((p) => p !== progresso);
    this.loading = false;

    const ok = this.processos.filter((p) => p.status !== 'BLOQUEADO').length;
    const falhas = this.processos.length - ok;

    if (falhas === 0) {
      this.poNotificationService.success(
        `${ok} PDF(s) enviado(s). Acompanhe o processamento abaixo.`
      );
      this.uploadPdf?.clear();
    } else if (ok === 0) {
      this.poNotificationService.error('Nenhum PDF foi enviado com sucesso. Verifique os detalhes.');
    } else {
      this.poNotificationService.warning(
        `${ok} PDF(s) enviado(s) e ${falhas} item(ns) com erro. Verifique os detalhes abaixo.`
      );
    }

    this.tentarFinalizarProgresso();
  }

  /** Envia um lote e devolve as linhas da tela: uma por PDF enviado ao Domínio e uma por arquivo com erro. */
  private async enviarLote(lote: DpPdfLoteEnvio, uploads: Map<string, ArquivoUpload>): Promise<DpPdfProcessoItem[]> {
    const marcar = (nome: string, status: number) => {
      const upload = uploads.get(nome);
      if (upload) upload.status = status;
    };

    let resultado: ImportDpLoteResult;
    try {
      resultado = await firstValueFrom(this.dpFichasService.importarPdfs(lote.arquivos));
    } catch (err: unknown) {
      const e = err as { error?: { error?: string; message?: string }; message?: string };
      lote.arquivos.forEach((a) => marcar(a.nome, 2));
      return [
        {
          nomeArquivo: lote.nome,
          status: 'BLOQUEADO',
          mensagem: e?.error?.error || e?.error?.message || e?.message || 'Erro ao enviar os PDFs.'
        }
      ];
    }

    const arquivosComErro = (resultado.arquivos ?? []).filter((a) => String(a.error || '').trim());
    const nomesComErro = new Set(arquivosComErro.map((a) => a.nomeArquivo));
    lote.arquivos.forEach((a) => marcar(a.nome, nomesComErro.has(a.nome) ? 2 : 0));

    const documentos = (resultado.documentos ?? []).map((documento) => {
      const item: DpPdfProcessoItem = {
        nomeArquivo: documento.nomeArquivo || documento.razaoSocial || documento.cnpj || 'PDF',
        status: 'PROCESSANDO',
        mensagem: ''
      };
      this.aplicarResultadoUpload(item, documento);
      return item;
    });

    const erros: DpPdfProcessoItem[] = arquivosComErro.map((a) => ({
      nomeArquivo: a.nomeArquivo || 'Arquivo',
      status: 'BLOQUEADO',
      layout: a.layout,
      mensagem: a.error || 'Erro ao ler o PDF.'
    }));

    const itens = [...documentos, ...erros];
    if (!itens.length) {
      itens.push({ nomeArquivo: lote.nome, status: 'BLOQUEADO', mensagem: 'Nenhum funcionário encontrado nos PDFs.' });
    }
    return itens;
  }

  private aplicarResultadoUpload(item: DpPdfProcessoItem, response: ImportDpPdfResult): void {
    item.layout = response.layout;
    item.avisos = response.avisos ?? [];
    item.cnpj = response.cnpj;
    item.razaoSocial = response.razaoSocial;
    item.codDominio = response.codDominio;
    item.requestId = response.requestId;
    item.totalEmpregados = response.totalEmpregados ?? 0;
    item.totalEnviados = response.totalEnviados ?? 0;

    const erro = String(response.error || '').trim();
    if (erro || !(response.totalEnviados && response.totalEnviados > 0)) {
      item.status = 'BLOQUEADO';
      item.mensagem = erro || 'PDF processado, mas nenhum empregado foi enviado.';
      return;
    }

    item.status = 'PROCESSANDO';
    const empresa = response.razaoSocial || response.cnpj || 'empresa';
    item.mensagem =
      `${response.totalEnviados} empregado(s) enfileirado(s) — ${empresa}` +
      (response.codDominio != null ? ` (Domínio ${response.codDominio})` : '') +
      '. Aguardando retorno do connector...';
  }

  private iniciarPolling(item: DpPdfProcessoItem): void {
    const requestId = item.requestId;
    if (!requestId) return;

    this.pollSubs.get(requestId)?.unsubscribe();

    let tentativas = 0;
    const sub = timer(0, this.pollIntervalMs)
      .pipe(
        exhaustMap(() => {
          tentativas++;
          return this.dpFichasService.obterResultado(requestId);
        }),
        takeWhile((res) => {
          const status = String(res?.status ?? '').toUpperCase();
          const pendente = !status || status === 'PENDING';
          return pendente && tentativas < this.pollMaxTentativas;
        }, true)
      )
      .subscribe({
        next: (res) => this.aplicarResultadoConsulta(item, res),
        error: () => {
          item.mensagem = `${item.mensagem} (falha ao consultar status; tente novamente depois.)`;
          this.pollSubs.delete(requestId);
          this.tentarFinalizarProgresso();
        },
        complete: () => {
          this.pollSubs.delete(requestId);
          if (item.status === 'PROCESSANDO') {
            item.mensagem =
              'Ainda processando no Domínio. O retorno pode chegar por notificação.';
          }
          this.tentarFinalizarProgresso();
        }
      });

    this.pollSubs.set(requestId, sub);
  }

  private aplicarResultadoConsulta(item: DpPdfProcessoItem, res: DpFichaConsultaResultado): void {
    const status = String(res?.status ?? '').toUpperCase();
    const resultado = res?.resultado;

    if (!status || status === 'PENDING') {
      item.status = 'PROCESSANDO';
      item.mensagem = 'Processando no Domínio...';
      return;
    }

    item.atualizados = resultado?.atualizados ?? 0;
    item.falhas = resultado?.falhas ?? 0;

    if (status === 'SUCCESS' || status === 'OK') {
      item.status = 'PRONTO';
      item.mensagem = `${item.atualizados} empregado(s) atualizado(s) com sucesso.`;
      return;
    }

    if (status === 'PARTIAL' || status === 'PARCIAL') {
      item.status = 'PARCIAL';
      item.mensagem =
        `${item.atualizados} atualizado(s), ${item.falhas} com falha.` +
        (resultado?.erro ? ` ${resultado.erro}` : '');
      return;
    }

    item.status = 'BLOQUEADO';
    item.mensagem = resultado?.erro || `Processamento finalizado com status ${status}.`;
  }

  private tentarFinalizarProgresso(): void {
    const aindaProcessando =
      this.loading ||
      this.processos.some((p) => p.status === 'PROCESSANDO') ||
      this.pollSubs.size > 0;

    if (!aindaProcessando && !this.fimProcessamento) {
      this.fimProcessamento = new Date();
    }
  }

  private pararTodosPollings(): void {
    for (const sub of this.pollSubs.values()) {
      sub.unsubscribe();
    }
    this.pollSubs.clear();
  }
}
